import { SupabaseClient } from '@supabase/supabase-js'
import { Database, Json, TablesUpdate } from "@/supabase/types"
import { TablesInsert } from "@/supabase/types"
import { PermOptions, PermAction } from '@/types/base/permissions'
import { Role } from "@/types/base/authuser"
import Authuser from '@/types/base/authuser'
import { isAidonAdmin, isTenantAdmin } from './tenants'
import { createIsomorphicLogger } from '@/utils/logger/isomorphicLogger'
import { logError } from '@/utils/errorLogging'
import { printObject } from '@/lib/utils'

const logger = createIsomorphicLogger('db.tenantUsers')

interface TenantUsersMetadata {
	active?: boolean
}

export interface User {
  id: string;
  username: string;
}

export interface TenantUsers {
  id: string;
  name: string;
  users: User[];
}

export const getAllProfileUsers = async (supabase: SupabaseClient<Database>) => {
  const { data: profiles, error } = await supabase
    .from("profiles")
    .select(
      `
      id,
      username
      `
    )

  if (!profiles) {
    logError(logger, error, 'Failed to fetch all profile Users')
    throw new Error(error.message)
  }

  return profiles;
}

export const getTenantUsersById = async (supabase: SupabaseClient<Database>, id: string) => {
	// eslint-disable-next-line prefer-const
  let { data: tenantUsers, error } = await supabase
    .from("tenant_users")
    .select("*")
    .eq("id", id)
		.single()

	if (error) {
    logError(logger, error, `Error in getTenantUsersById for ID: ${id}`, { message: error.message })
		tenantUsers = null
	}

  return tenantUsers
}

export const getTenantUsersByTenantId = async (supabase: SupabaseClient<Database>, tenantId: string) => {
  const { data: tenantUsers, error } = await supabase
    .from("tenants")
    .select(
      `
        id,
        name,
        users:tenant_users(tu_id:id, metadata, ...profiles( id, user_id, username, display_name ))
      `
    )
    .eq("id", tenantId)
    .single()

  if (!tenantUsers) {
    logError(logger, error, 'Failed to fetch Tenant Users by Tenant ID', { tenantId })
    throw new Error(error.message)
  }

  return tenantUsers  // as TenantUsers[]
}

export const getTenantUsersByUserId = async (supabase: SupabaseClient<Database>, userId: string) => {
	// eslint-disable-next-line prefer-const
  let { data: tenantUsers, error } = await supabase
    .from("tenant_users")
    .select("*")
    .eq("user_id", userId)
		.maybeSingle()

	if (error) {
    logError(logger, error, `Error in getTenantUsersByUserId for ID: ${userId}`, { message: error.message })
		tenantUsers = null
	}
	else if (!tenantUsers) {
		logger.warn(`Attempt to getTenantUsersByUserId for ID: ${userId} resulted in no content found`)
		tenantUsers = null
	}

  return tenantUsers
}

export const createTenantUser = async (
	supabase: SupabaseClient<Database>,
  tenantUser: TablesInsert<"tenant_users">
) => {
  const { data: createdTenantUser, error } = await supabase
    .from("tenant_users")
    .insert(tenantUser)
    .select("*")

  if (!createdTenantUser) {
    logError(logger, error, 'Failed to create Tenant User', { tenantUser: printObject(tenantUser) })
    throw new Error(error.message)
  }

  logger.info('Tenant User created successfully', { tenantUser: printObject(createdTenantUser) })
  return createdTenantUser
}

export const createTenantUsers = async (
	supabase: SupabaseClient<Database>,
  tenantUsers: TablesInsert<"tenant_users">[]
) => {
  const { data: createdTenantUsers, error } = await supabase
    .from("tenant_users")
    .insert(tenantUsers)
    .select("*")

  if (!createdTenantUsers) {
    logError(logger, error, 'Failed to create Tenant Users', { tenantUsers: printObject(tenantUsers) })
    throw new Error(error.message)
  }

  logger.info('Tenant Users created successfully', { tenantUsers: createdTenantUsers.map(user => user.id) })
  return createdTenantUsers
}

export const updateTenantUser = async (
	supabase: SupabaseClient<Database>,
	tenantUserId: string,
	tenantUser: TablesUpdate<"tenant_users">
) => {

	const { data: updatedTenantUser, error } = await supabase
		.from("tenant_users")
		.update(tenantUser)
		.eq("id", tenantUserId)
		.select("*")
		.single()

	if (error) {
		logError(logger, error, 'Error in updating TenantUsers', { tenantUserId, tenantUser })
		throw new Error(error.message)
	}

	return updatedTenantUser
}

export const deleteTenantUser = async (
	supabase: SupabaseClient<Database>,
  tenantId: string,
  profileId: string
) => {
  const { error } = await supabase
    .from("tenant_users")
    .delete()
    .eq("tenant_id", tenantId)
    .eq("tenant_user_id", profileId)

  if (error) {
    logError(logger, error, 'Failed to delete Tenant User', { tenantId, profileId })
    throw new Error(error.message)
  }

  return true
}

/* Active is true by default and doesn't need an entry in metadata. Meant in teams to disable a user without removing it tenantUsers */
export const isUserActive = (tenantUsers: TablesUpdate<"tenant_users"> | null) => {
	const prMeta = tenantUsers?.metadata as TenantUsersMetadata | undefined;
	return prMeta?.active?? true
}

export const isJsonUserActive = (metadata: Json | undefined) => {
	const prMeta = metadata as TenantUsersMetadata | undefined;
	return prMeta?.active?? true
}

export const setUserActive = (
	metadata: Json,
	active: boolean
) => {
	const prMeta: TenantUsersMetadata = metadata ? { ...metadata as TenantUsersMetadata } : {}
	prMeta.active = active;
	return prMeta as Json
}

export const tenantUsersCanPerform = async (supabase: SupabaseClient<Database>, authuser: Authuser, options: PermOptions) => {
	if (authuser.isAuthorizedForRole(Role.root)) {
		return true
	}

	switch (options.action) {
		case PermAction.select:
			if (await isAidonAdmin(supabase, authuser)) {
				if (options?.context === 'true') {
					return true
				}
				if (options?.uuid) {
					return (await getTenantUsersByUserId(supabase, options.uuid)) == null  //not used by any other tenant
				}
			}
			if (await isTenantAdmin(authuser)) {
				if (options?.context === "true") {
					return true
				}
				if (options?.relUuid === await authuser.getTenantId(supabase)) {
					return true
				}
			}
			return false
		default:
			return true
	}
}